import {useEventPageContentQuery} from './__generated__/Event.graphql';
import Detail from './Detail';
import EventExtraInfo from './EventExtraInfo';
import EventInfo from './EventInfo';
import EventThumbnail from './EventThumbnail';
import EventTitleWithModifier from './EventTitleWIthModifier';
import RegistrationFormCard from './RegistrationFormCard';
import {
  GroupSessionPermission,
  GroupSessionRequestStatus,
} from '../../../__generated__/graphql/types';
import NotFoundPageContent from '../../../pages/NotFound/NotFoundPageContent';
import Button from '../design_system/Button';
import {ButtonColor} from '../design_system/Button/options';
import useAppTheme from '../design_system/hooks/useAppTheme';
import useMediaQueryGetter, {
  MediaQuery,
} from '../design_system/hooks/useMediaQueryGetter';
import ContentLoadingProgress from '../design_system/progress/ContentLoadingProgress';
import Typography, {Size} from '../design_system/typography/Typography';
import Flex from '../layout/flex/Flex';

import {css} from '@emotion/react';
import React from 'react';
import {useTranslation} from 'react-i18next';

export interface Props {
  readonly id: string;
}

const SHOW_FORM_HASH = '#registration-form';

export default function EventPageContent(props: Props): JSX.Element {
  const {id} = props;
  const {data, loading: isLoading} = useEventPageContentQuery({
    variables: {
      id,
    },
  });
  const getMediaQuery = useMediaQueryGetter();
  const theme = useAppTheme();
  const {t} = useTranslation();
  const groupSession = data?.groupSession;
  const isRegistered = (data?.viewer?.registeredEvents.totalCount ?? 0) > 0;

  const groupSessionRequests = data?.viewer?.groupSessionRequests;
  const firstEdge = groupSessionRequests?.edges?.[0];
  const node = firstEdge?.node;
  const registeredStatus = node?.status as
    | GroupSessionRequestStatus
    | undefined;

  if (isLoading) {
    return (
      <div
        css={css({
          textAlign: 'center',
          marginTop: 100,
          height: '85vh',
        })}
      >
        <ContentLoadingProgress />
      </div>
    );
  } else if (
    groupSession == null ||
    groupSession.__typename !== 'GroupSession'
  ) {
    return <NotFoundPageContent />;
  } else {
    const extraData = EventExtraInfo[groupSession.id];

    const isClosed = groupSession.permission === GroupSessionPermission.Closed;

    const renderRegistrationFormCard = () => {
      if (!isClosed && groupSession) {
        return (
          <RegistrationFormCard
            event={groupSession}
            eventExtraData={{
              checkInCode: extraData?.checkInCode,
              surveyUrl: extraData?.surveyUrl,
            }}
            formHash={SHOW_FORM_HASH}
            isRegistered={isRegistered}
            registeredStatus={registeredStatus}
            viewer={data.viewer}
          />
        );
      }
    };

    const renderDetails = () => {
      return extraData?.details.map((item, i) => {
        return (
          <div
            css={css({
              ':not(:first-child)': {
                marginTop: '40px',
              },
            })}
            key={i}
          >
            {item.type === 'registration-form' ? (
              <div
                css={css({
                  [getMediaQuery(MediaQuery.LargeAndExtraLarge)]: {
                    display: 'none',
                  },
                })}
              >
                {renderRegistrationFormCard()}
              </div>
            ) : (
              <Detail detail={item} />
            )}
          </div>
        );
      });
    };

    return (
      <Flex
        direction="column"
        sx={css({
          gap: '20px',
          marginTop: '20px',
        })}
      >
        <Flex
          sx={css({
            gap: '20px',
            width: '100%',
            flexDirection: 'column',
            [getMediaQuery(MediaQuery.LargeAndExtraLarge)]: {
              textAlign: 'left',
            },
          })}
        >
          <Flex
            data-testid="event-title"
            sx={css({
              width: '100%',
            })}
          >
            <Typography
              size={Size.XXL}
              variant="h1"
            >
              <EventTitleWithModifier groupSession={groupSession} />
            </Typography>
          </Flex>

          <Flex
            sx={css({
              width: '100%',
              gap: '20px',
              flexDirection: 'column',
              [getMediaQuery(MediaQuery.LargeAndExtraLarge)]: {
                flexDirection: 'row',
              },
            })}
          >
            <EventInfo
              data={groupSession}
              extraData={extraData}
            />

            {!isClosed && (
              <Flex
                sx={css({
                  width: '100%',
                  [getMediaQuery(MediaQuery.LargeAndExtraLarge)]: {
                    width: '250px',
                  },
                })}
              >
                <Button
                  color={ButtonColor.ACCENT}
                  label={t('pages.event.register-for-event')}
                  sx={css({
                    width: '100%',
                  })}
                  to={SHOW_FORM_HASH}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
        <Flex
          sx={css({
            justifyContent: 'center',
            width: '100%',
          })}
        >
          <EventThumbnail extraData={extraData} />
        </Flex>

        <Flex
          css={css({
            alignItems: 'start',
            gap: '20px',
            paddingBottom: '80px',
            flexDirection: 'row',
          })}
        >
          <div
            css={css({
              flex: 1,
              wordBreak: 'break-all',
            })}
          >
            {renderDetails()}
          </div>
          <div
            css={css({
              display: 'none',
              [getMediaQuery(MediaQuery.LargeAndExtraLarge)]: {
                display: 'block',
                top: theme.sizes.l.topNavHeight + 20,
                position: 'sticky',
                width: '380px',
              },
            })}
          >
            {renderRegistrationFormCard()}
          </div>
        </Flex>
      </Flex>
    );
  }
}
